import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePartnerUnread } from '../context/PartnerUnreadContext';
import { useSettings } from '../context/SettingsContext';

export default function PartnerLayout({ children }) {
  const { admin, logout }                  = useAuth();
  const { theme, toggleTheme, lang, setLang, t } = useSettings();
  const { unread, clearUnread }            = usePartnerUnread();   // ← from context
  const navigate  = useNavigate();
  const location  = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Clear badge immediately when visiting the requests page
  useEffect(() => {
    if (location.pathname === '/partner/requests' && unread > 0) {
      clearUnread();
    }
  }, [location.pathname, unread, clearUnread]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const navItems = [
    { to: '/partner',          label: t('myRestaurants'), icon: '◫', exact: true },
    { to: '/partner/requests', label: t('myRequests'),    icon: '◈', badge: unread },
  ];

  const SidebarContent = () => (
    <>
      {/* Logo only — no text, company name below if present */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '16px 16px', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <img src="/logo.png" alt="Inone" style={{ height: 44, width: 'auto', objectFit: 'contain' }} />
          {admin?.companyName && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin.companyName}</div>
          )}
        </div>
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1, padding: '0 4px', marginLeft: 'auto' }}>×</button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '0 10px' }}>
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} end={item.exact}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              color: isActive ? 'var(--accent)' : 'var(--text-muted)',
              background: isActive ? 'var(--accent-glow)' : 'transparent',
              fontSize: 14, fontWeight: isActive ? 500 : 400,
              marginBottom: 4, transition: 'all 0.15s',
            })}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
            <span>{item.label}</span>
            {item.badge > 0 && (
              <span style={{ marginLeft: 'auto', background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px', flexShrink: 0 }}>
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom: theme, lang, user, logout */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '16px 14px 12px' }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7, paddingLeft: 2 }}>Theme</div>
          <div className="toggle-pill">
            <button className={theme === 'dark'  ? 'active' : ''} onClick={() => theme !== 'dark'  && toggleTheme()}>🌙 {t('darkMode')}</button>
            <button className={theme === 'light' ? 'active' : ''} onClick={() => theme !== 'light' && toggleTheme()}>☀️ {t('lightMode')}</button>
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7, paddingLeft: 2 }}>Language</div>
          <div className="toggle-pill">
            {[['en','EN'],['ru','RU'],['uz','UZ']].map(([code, label]) => (
              <button key={code} className={lang === code ? 'active' : ''} onClick={() => setLang(code)}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 2 }}>{t('signedInAs')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin?.username}</div>
            <span style={{ fontSize: 10, background: 'rgba(100,160,255,0.15)', color: '#6ab0ff', padding: '2px 7px', borderRadius: 10, fontWeight: 600, flexShrink: 0 }}>{t('rolePartner')}</span>
          </div>
        </div>
        <button onClick={handleLogout} className="btn-ghost" style={{ width: '100%', textAlign: 'left', fontSize: 13 }}>
          ↪ {t('signOut')}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <header className="mobile-topbar">
        <button className="hamburger" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <span /><span /><span />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/logo.png" alt="Inone" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{t('appName')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {unread > 0 && (
            <span style={{ background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>{unread}</span>
          )}
          <button onClick={toggleTheme} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, width: 36, height: 36, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </header>

      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <div className="layout-shell">
        <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
          <SidebarContent />
        </aside>
        <main className="layout-main">{children}</main>
      </div>
    </>
  );
}
